<template>
  <v-card class="ui-module-card ui-module-fade-in-up personal-activo-shell" color="primary">
    <v-card-text>
      <v-container fluid>
        <v-row>
          <v-col cols="12">
            <v-row align="center" class="mx-0 toolbar-row">
              <v-col cols="12" md="6">
                <BaseInput
                  v-model="search"
                  label="Buscar por nombre o CUIP"
                  append-icon="mdi-magnify"
                  class="search-input"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-select
                  v-model="puestoFiltro"
                  :items="puestoOptions"
                  item-text="text"
                  item-value="value"
                  label="Puesto"
                  color="secondary"
                  background-color="primary"
                  clearable
                  dense
                  filled
                  rounded
                />
              </v-col>
            </v-row>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12">
            <v-card class="ui-module-card" color="primary" :loading="loading">
              <v-card-text>
                <v-data-table :headers="headers" :items="filteredPersonal" class="transparent armory-table">
                  <template v-slot:[`item.nombre`]="{ item }">
                    {{ item.nombreCompleto }}
                  </template>
                  <template v-slot:[`item.puesto`]="{ item }">
                    {{ item.puesto ? item.puesto : '' }}
                  </template>
                  <template v-slot:[`item.CUIP`]="{ item }">
                    <span>{{ item.CUIP ? item.CUIP : 'CUIP no asignado' }}</span>
                  </template>
                  <template v-slot:[`item.opciones`]="{ item }">
                    <span class="d-flex align-center action-group">
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                          <span v-bind="attrs" v-on="on">
                            <IconButton
                              class="mr-2"
                              variant="secondary"
                              elevation="8"
                              icon="mdi-card-account-details-outline"
                              :to="{
                                path: 'documentos_personal/' + item.id,
                                params: { id: item.id },
                              }"
                            />
                          </span>
                        </template>
                        <span>Ver expediente</span>
                      </v-tooltip>
                    </span>
                  </template>
                </v-data-table>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </v-card-text>
  </v-card>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import IconButton from '../../components/base/IconButton.vue'
import BaseInput from '../../components/base/BaseInput.vue'

// Mapeo de datos:
// - personalLite (store `personal`) → remapeado por fetchPersonalByDependenciaUsecase:
//   { id_personal, idPersonal, nombre, segundoNombre, apaterno, amaterno, CUIP, puesto (string), ... }
// - puestos (store `puesto`) → catálogo: { id, catPuesto: { puesto: '<nombre>' } }
// El filtro por puesto compara strings (item.puesto === puestoFiltro).
export default {
  components: {
    IconButton,
    BaseInput,
  },

  data() {
    return {
      headers: [
        { text: 'Nombre', value: 'nombre', width: '300px' },
        { text: 'Puesto', value: 'puesto', width: '250px' },
        { text: 'CUIP', value: 'CUIP', width: '180px' },
        { text: 'Acciones', value: 'opciones', sortable: false, width: '140px' },
      ],
      loading: false,
      search: '',
      puestoFiltro: null,
    }
  },

  computed: {
    ...mapGetters('personal', {
      personalLite: 'getPersonalLite',
    }),
    ...mapGetters('puesto', {
      puestos: 'getPuestos',
    }),

    personalRows() {
      return (this.personalLite || []).map((p) => ({
        ...p,
        id: p.id_personal ?? p.idPersonal,
        nombreCompleto: this.buildFullName(p),
      }))
    },

    puestoOptions() {
      const nombres = (this.puestos || [])
        .map((p) => p?.catPuesto?.puesto)
        .filter(Boolean)
      return Array.from(new Set(nombres))
        .sort((a, b) => a.localeCompare(b))
        .map((n) => ({ text: n, value: n }))
    },

    filteredPersonal() {
      const term = (this.search || '').trim().toLowerCase()
      const puesto = this.puestoFiltro
      return (this.personalRows || []).filter((p) => {
        if (puesto && p.puesto !== puesto) return false
        if (!term) return true
        const nombre = (p.nombreCompleto || '').toLowerCase()
        const cuip = (p.CUIP || '').toLowerCase()
        if (nombre.includes(term)) return true
        if (cuip && cuip.includes(term)) return true
        return false
      })
    },
  },

  methods: {
    ...mapActions('personal', ['fetchPersonalLite']),
    ...mapActions('puesto', ['fetchPuestos']),

    buildFullName(p) {
      if (!p) return ''
      return [p.nombre, p.segundoNombre, p.apaterno, p.amaterno].filter(Boolean).join(' ')
    },
  },

  async mounted() {
    const needsPersonal = !this.personalLite || this.personalLite.length === 0
    const needsPuestos = !this.puestos || this.puestos.length === 0
    if (needsPersonal || needsPuestos) {
      this.loading = true
      try {
        const jobs = []
        if (needsPersonal) jobs.push(this.fetchPersonalLite())
        if (needsPuestos) jobs.push(this.fetchPuestos())
        await Promise.all(jobs)
      } finally {
        this.loading = false
      }
    }
  },
}
</script>

<style scoped>
.personal-activo-shell {
  min-height: calc(100vh - 64px);
  min-height: calc(100dvh - 64px);
}

.ui-module-card {
  background: rgba(30, 30, 46, 0.6) !important;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 1px solid rgba(108, 92, 231, 0.2);
  border-radius: 20px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  transition: all 0.3s ease;
}

::v-deep .v-data-table {
  background: transparent !important;
  color: #e0e0e0 !important;
}

::v-deep .v-data-table-header {
  background: rgba(108, 92, 231, 0.15) !important;
}

::v-deep .v-data-table-header th {
  color: #e0e0e0 !important;
  font-weight: 600 !important;
  border-bottom: 2px solid rgba(108, 92, 231, 0.3) !important;
}

::v-deep .v-data-table tbody tr {
  transition: all 0.3s ease;
}

::v-deep .v-data-table tbody tr:hover {
  background: rgba(108, 92, 231, 0.1) !important;
}

::v-deep .v-data-table tbody td {
  color: #e0e0e0 !important;
  border-bottom: 1px solid rgba(108, 92, 231, 0.1) !important;
}

.action-group {
  gap: 4px;
}

::v-deep .action-group .v-btn {
  border: 1px solid rgba(108, 92, 231, 0.25) !important;
  background: rgba(108, 92, 231, 0.08) !important;
}

.toolbar-row {
  row-gap: 8px;
}
</style>
